import React, { useRef, useState } from 'react';
import { Permissions } from '../../config';
import {
    Avatar,
    Box,
    Typography,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Skeleton,
} from '@mui/material';
import { createUseStyles } from 'react-jss';
import { MoreVert, Edit, Delete } from '@mui/icons-material';
import { useTable, useFilters, useExpanded } from 'react-table';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { formatKennitala } from '../../utils/kennitalaFormatter';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import SparaButton from 'components/SparaButton/SparaButton';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';

import exStyles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';
import styles from '../../assets/jss/material-dashboard-pro-react/components/tableStyle';

function DefaultColumnFilter({ column: { Header, filterValue, setFilter } }) {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
}

const TextColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const PhoneColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const KennitalaColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const PositionColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const RolesColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const CompanyColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const GroupsColumnFilter = ({ column: { Header, filterValue, setFilter } }) => {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
};

const style = {
    tableTitle: {
        position: 'absolute',
        top: '10px',
        color: '#09b1c5',
        fontWeight: 'bold',
        fontSize: '18px',
    },
    tableBodyRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: 'var(--spara-table-row-alt, #f9f9f9)',
        },
        '&:nth-of-type(even)': {
            backgroundColor: 'var(--spara-table-row, #fff)',
        },
    },
    tableResponsive: {
        // width: "100%",
        minHeight: '0.1%',
        overflowX: 'auto',
    },
    ...exStyles,
    ...styles,
};

const useStyles = createUseStyles(style);

const generateColorFromId = (id) => {
    if (!id) return 'var(--spara-surface-2, #cccccc)';
    const hash = id
        .toString()
        .split('')
        .reduce((acc, char) => {
            return (acc * 31 + char.charCodeAt(0)) % 360;
        }, 0);
    return `hsl(${hash}, 55%, var(--spara-avatar-bg-l, 88%))`;
};

const generateTextColorFromId = (id) => {
    if (!id) return 'var(--spara-text-muted, #666666)';
    const hash = id
        .toString()
        .split('')
        .reduce((acc, char) => {
            return (acc * 31 + char.charCodeAt(0)) % 360;
        }, 0);
    return `hsl(${hash}, 55%, var(--spara-avatar-fg-l, 30%))`;
};

const getRoleColor = (roleName) => {
    switch (roleName.toLowerCase()) {
        case 'company_user':
            return {
                bgcolor: '#e3f2fd', // Ljósblár
                color: '#1976d2', // Dökkblár
            };
        case 'super_admin':
            return {
                bgcolor: '#fce4ec', // Ljósrauður
                color: '#c2185b', // Dökkrauður
            };
        case 'group_admin':
            return {
                bgcolor: '#e8f5e9', // Ljósgrænn
                color: '#2e7d32', // Dökkgrænn
            };
        default:
            return {
                bgcolor: '#f5f5f5', // Ljósgrár
                color: '#616161', // Dökkgrár
            };
    }
};

const SKELETON_ROWS = 8;

const TableRowSkeleton = ({ columnCount }) => (
    <TableRow>
        <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={18} />
                    <Skeleton variant="text" width="80%" height={14} sx={{ mt: 0.5 }} />
                </Box>
            </Box>
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width="70%" />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width="80%" />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width="50%" />
        </TableCell>
        <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 20 }} />
            </Box>
        </TableCell>
        <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="rounded" width={100} height={22} sx={{ borderRadius: 20 }} />
                <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 20 }} />
            </Box>
        </TableCell>
        <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: 20 }} />
            </Box>
        </TableCell>
        <TableCell align="right">
            <Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} />
        </TableCell>
    </TableRow>
);

const CompanyUsersTable = (props) => {
    const classes = useStyles();
    const userStore = JSON.parse(localStorage.getItem('user') || '{}');
    const userPermission = userStore.permission ?? [];
    const COMPANY_USER_UPDATE = userPermission.includes(Permissions.COMPANY_USER_UPDATE) ||
        userPermission.includes(Permissions.CU_COMPANY_USER_UPDATE);
    const COMPANY_USER_DELETE = userPermission.includes(Permissions.COMPANY_USER_DELETE) ||
        userPermission.includes(Permissions.CU_COMPANY_USER_DELETE);
    const data = props.data;
    const loading = props.loading;
    const { t } = useTranslation();

    const roles = userStore.roles ?? [];
    const isAdmin = roles.includes('super_admin');
    const hasCompanies = isAdmin || (userStore.companyIds?.filter(Boolean).length > 0);
    const hasGroups = isAdmin || (userStore.groups?.length > 0);
    const [visibleCount, setVisibleCount] = useState(25);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });

    const handleMenuClick = (event, user) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedUser(null);
    };

    const handleDeleteUser = (userId) => {
        setDeleteDialog({ open: true, userId });
    };

    const confirmDelete = () => {
        props.onDelete({ id: deleteDialog.userId });
        setDeleteDialog({ open: false, userId: null });
        handleMenuClose();
    };

    const handleEditUser = (user) => {
        props.onUpdate(user);
        handleMenuClose();
    };

    const columns = React.useMemo(
        () => [
            {
                Header: t('user'),
                accessor: (values) => {
                    let value = values.children[0];
                    const name = value.torgid_user ? value.torgid_user.name : '';
                    const email = value.torgid_user ? value.torgid_user.email : '';
                    const userId = value.torgid_user ? value.torgid_user.id : value.id;
                    const profilePicture = value.torgid_user?.profile_picture;

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                            <Avatar
                                src={profilePicture}
                                title={`User ID: ${userId}`}
                                sx={{
                                    mr: 2,
                                    bgcolor: generateColorFromId(userId),
                                    color: generateTextColorFromId(userId),
                                }}
                            >
                                {name ? name.charAt(0).toUpperCase() : '?'}
                            </Avatar>
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography sx={{ textAlign: 'left' }}>
                                    {name || t('no_name')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ textAlign: 'left' }}
                                >
                                    {email || t('no_email')}
                                </Typography>
                            </Box>
                        </Box>
                    );
                },
                Filter: TextColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const user = row.original.children[0].torgid_user;
                        const name = user?.name || '';
                        const email = user?.email || '';
                        return (
                            name.toLowerCase().includes(filterValue.toLowerCase()) ||
                            email.toLowerCase().includes(filterValue.toLowerCase())
                        );
                    });
                },
            },
            {
                Header: t('phone'),
                accessor: (values) => {
                    const phone = values.children[0].torgid_user?.phone;
                    return (
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                color: phone ? 'inherit' : 'text.secondary',
                                fontSize: phone ? 'inherit' : '0.75rem',
                                opacity: phone ? 1 : 0.5,
                                fontStyle: phone ? 'normal' : 'italic',
                            }}
                        >
                            {phone ? formatPhoneNumber(phone) : t('no_phone')}
                        </Typography>
                    );
                },
                Filter: PhoneColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const phone = row.original.children[0].torgid_user?.phone || '';
                        const formattedPhone = formatPhoneNumber(phone);
                        return phone.includes(filterValue) || formattedPhone.includes(filterValue);
                    });
                },
            },
            {
                Header: t('kennitala'),
                accessor: (values) => {
                    const kennitala = values.children[0].torgid_user?.kennitala;
                    return (
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                color: kennitala ? 'inherit' : 'text.secondary',
                                fontSize: kennitala ? 'inherit' : '0.75rem',
                                opacity: kennitala ? 1 : 0.5,
                                fontStyle: kennitala ? 'normal' : 'italic',
                            }}
                        >
                            {kennitala ? formatKennitala(kennitala) : t('no_kennitala')}
                        </Typography>
                    );
                },
                Filter: KennitalaColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const kennitala = row.original.children[0].torgid_user?.kennitala || '';
                        const formattedKennitala = formatKennitala(kennitala);
                        return (
                            kennitala.includes(filterValue) ||
                            formattedKennitala.includes(filterValue)
                        );
                    });
                },
            },
            {
                Header: t('position'),
                accessor: (values) => {
                    const position = values.children[0].torgid_user?.position;
                    return (
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                color: position ? 'inherit' : 'text.secondary',
                                fontSize: position ? 'inherit' : '0.75rem',
                                opacity: position ? 1 : 0.5,
                                fontStyle: position ? 'normal' : 'italic',
                            }}
                        >
                            {position || t('no_position')}
                        </Typography>
                    );
                },
                Filter: PositionColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const position = row.original.children[0].torgid_user?.position || '';
                        return position.toLowerCase().includes(filterValue.toLowerCase());
                    });
                },
            },
            {
                Header: t('roles'),
                accessor: (values) => {
                    const userRoles = values.children[0].torgid_user?.roles ?? [];
                    const visibleRoles = userRoles.filter((role) => {
                        if (role.name === 'company_user' && !hasCompanies) return false;
                        if (role.name === 'group_admin' && !hasGroups) return false;
                        return true;
                    });
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {visibleRoles.map((role) => {
                                const colors = getRoleColor(role.name);
                                return (
                                    <Chip
                                        key={role.id}
                                        label={t(`roles.${role.name.toLowerCase()}`)}
                                        size="small"
                                        sx={{
                                            bgcolor: colors.bgcolor,
                                            color: colors.color,
                                            '&:hover': {
                                                bgcolor: colors.bgcolor,
                                                opacity: 0.9,
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    );
                },
                Filter: RolesColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const roles = row.original.children[0].torgid_user?.roles || [];
                        return roles.some((role) => {
                            const translatedName = t(
                                `roles.${role.name.toLowerCase()}`
                            ).toLowerCase();
                            const originalName = role.name.toLowerCase();
                            return (
                                translatedName.includes(filterValue.toLowerCase()) ||
                                originalName.includes(filterValue.toLowerCase())
                            );
                        });
                    });
                },
            },
            {
                _colId: 'company',
                Header: t('company'),
                accessor: (values) => {
                    if (!values.children || !values.children[0]) {
                        return null;
                    }
                    const companies = values.children
                        .filter(
                            (item) =>
                                item &&
                                item.torgid_company_id &&
                                (item.torgid_company_id !== 1305 || item.torgid_company_id === null)
                        )
                        .map((item) => item.torgid_company)
                        .filter(
                            (company, index, self) =>
                                company && index === self.findIndex((c) => c?.id === company?.id)
                        );

                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {companies.map((company) => {
                                if (!company) return null;

                                const isDeleted = company.deleted === true;
                                const isDisabled = company.disabled === true;

                                let chipProps = {
                                    key: company?.id,
                                    label: company?.name,
                                    size: 'small',
                                    avatar: company?.logo ? (
                                        <Avatar src={company.logo} alt={company.name} />
                                    ) : undefined,
                                };

                                if (isDeleted) {
                                    chipProps.label = `${company.name} (${t('deleted')})`;
                                    chipProps.color = 'error';
                                } else if (isDisabled) {
                                    chipProps.label = `${company.name} (${t('disabled')})`;
                                    chipProps.color = 'warning';
                                }

                                return <Chip {...chipProps} />;
                            })}
                        </Box>
                    );
                },
                Filter: CompanyColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const companies = row.original.children
                            .filter(
                                (item) =>
                                    item.torgid_company_id !== 1305 ||
                                    item.torgid_company_id === null
                            )
                            .map((item) => item.torgid_company);
                        return companies.some((company) =>
                            company?.name?.toLowerCase().includes(filterValue.toLowerCase())
                        );
                    });
                },
            },
            {
                _colId: 'groups',
                Header: t('menu.groups'),
                accessor: (values) => {
                    const groups = values.children
                        .map((item) => item.torgid_special_company)
                        .filter(
                            (company, index, self) =>
                                company && index === self.findIndex((c) => c?.id === company?.id)
                        );

                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {groups.map((group) =>
                                group?.deleted ? (
                                    <Chip
                                        key={group.id}
                                        label={`${group.name} (${t('deleted')})`}
                                        size="small"
                                        color="error"
                                        avatar={
                                            group.logo ? (
                                                <Avatar src={group.logo} alt={group.name} />
                                            ) : undefined
                                        }
                                    />
                                ) : (
                                    <Chip
                                        key={group.id}
                                        label={group.name}
                                        size="small"
                                        avatar={
                                            group.logo ? (
                                                <Avatar src={group.logo} alt={group.name} />
                                            ) : undefined
                                        }
                                    />
                                )
                            )}
                        </Box>
                    );
                },
                Filter: GroupsColumnFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const groups = row.original.children
                            .map((item) => item.torgid_special_company)
                            .filter(
                                (company, index, self) =>
                                    company &&
                                    index === self.findIndex((c) => c?.id === company?.id)
                            );

                        return groups.some((group) =>
                            group?.name?.toLowerCase().includes(filterValue.toLowerCase())
                        );
                    });
                },
            },
            {
                Header: t('actions'),
                disableFilters: true,
                Cell: ({ cell }) => {
                    const user = cell.row.original.children[0];
                    if (!COMPANY_USER_UPDATE && !COMPANY_USER_DELETE) return null;
                    return (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title={t('actions')}>
                                <IconButton onClick={(e) => handleMenuClick(e, user)}>
                                    <MoreVert />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                },
            },
        ].filter((col) => {
            if (col._colId === 'company' && !hasCompanies) return false;
            if (col._colId === 'groups' && !hasGroups) return false;
            return true;
        }),
        [hasCompanies, hasGroups] // eslint-disable-line
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
            defaultColumn: { Filter: DefaultColumnFilter },
            autoResetExpanded: false,
            disableSortBy: true,
        },
        useFilters,
        useExpanded
    );

    const visibleRows = rows.slice(0, visibleCount);
    const hasMore = visibleCount < rows.length;

    return (
        <React.Fragment>
            <ConfirmDialog
                open={deleteDialog.open}
                type="danger"
                title={t('are_you_sure')}
                message={t('delete_user_confirmation')}
                confirmText={t('delete_yes')}
                cancelText={t('cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, userId: null })}
            />
            <div className={classes.tableResponsive}>
                <Table className={classes.table}>
                    <TableHead>
                        {headerGroups.map((headerGroup) => (
                            <TableRow
                                className={classes.tableRow + ' ' + classes.tableRowHead}
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {headerGroup.headers.map((column) => (
                                    <TableCell
                                        variant="head"
                                        className={classes.tableHeadCell}
                                        {...column.getHeaderProps({
                                            style: { minWidth: column.minWidth },
                                        })}
                                    >
                                        <div>{column.render('Header')}</div>
                                        {column.canFilter && column.render('Filter')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {loading
                            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                                  <TableRowSkeleton key={i} />
                              ))
                            : visibleRows.map((row, key) => {
                                  prepareRow(row);
                                  return (
                                      <React.Fragment key={row.getRowProps().key}>
                                          <TableRow
                                              key={key}
                                              hover={COMPANY_USER_UPDATE}
                                              className={classes.tableBodyRow}
                                              onClick={(e) => {
                                                  if (!COMPANY_USER_UPDATE) return;
                                                  if (e.target.closest('.MuiIconButton-root'))
                                                      return;
                                                  props.onUpdate(row.original.children[0]);
                                              }}
                                              sx={{ cursor: COMPANY_USER_UPDATE ? 'pointer' : 'default' }}
                                          >
                                              {row.cells.map((cell) => (
                                                  <TableCell
                                                      className={classes.tableCell}
                                                      key={key}
                                                      {...cell.getCellProps()}
                                                  >
                                                      {cell.render('Cell')}
                                                  </TableCell>
                                              ))}
                                          </TableRow>
                                      </React.Fragment>
                                  );
                              })}
                    </TableBody>
                </Table>
            </div>

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                {COMPANY_USER_UPDATE && selectedUser && (
                    <MenuItem onClick={() => handleEditUser(selectedUser)}>
                        <Edit sx={{ mr: 1, color: 'primary.main' }} />
                        {t('edit')}
                    </MenuItem>
                )}
                {COMPANY_USER_DELETE && selectedUser && (
                    <MenuItem onClick={() => handleDeleteUser(selectedUser.id)}>
                        <Delete sx={{ mr: 1, color: 'error.main' }} />
                        {t('delete')}
                    </MenuItem>
                )}
            </Menu>

            {!loading && hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <SparaButton
                        variant="outlined"
                        color="primary"
                        onClick={() => setVisibleCount((c) => c + 25)}
                    >
                        {t('load_more')} ({rows.length - visibleCount})
                    </SparaButton>
                </Box>
            )}
        </React.Fragment>
    );
};

export default CompanyUsersTable;
